import { useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Bell, LogOut, ChevronDown, User } from "lucide-react";
import { getUserInitials } from "@/utils/userUtils";
import { useOutsideClick } from "@/hooks/useOutsideClick";
import { useAuth } from "@/auth/useAuth";
import { APP_MODULES, MODULE_INFO } from "@/auth/appModules";
import { cn } from "@/lib/utils";
import { usePendingApprovals } from "@/pages/adminDashboard/hooks/usePendingApprovals";

import mdfMark from "./../../assets/mdf-mark.png";

/**
 * Phones only: a slim bar with the MDF mark and the user menu, which also
 * holds the page links. On desktop the navigation rail (Sidebar) does this.
 */
const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation("header");

  const [showMenu, setShowMenu] = useState(false);
  const { status, me, logout, hasModule, isPlatformAdmin } = useAuth();
  const waiting = usePendingApprovals();

  const menuRef = useRef<HTMLDivElement | null>(null);

  const isAuthPage = ["/login", "/signup", "/auth/callback"].includes(location.pathname);
  const signedIn = !isAuthPage && status === "signedIn";

  const displayName = me?.name || me?.email?.split("@")[0] || t("header.user");
  const email = me?.email ?? null;

  // The pages this user may open (access groups), plus the admin panel.
  const links = [
    ...APP_MODULES.filter((m) => hasModule(m)).map((m) => MODULE_INFO[m]),
    ...(isPlatformAdmin ? [{ label: "Admin", path: "/admin" }] : []),
  ];
  // The longest matching path wins: /digitaltwin/scenarios is "Scenarios", not "Digital Twin".
  const activePath = links
    .map((l) => l.path)
    .filter((p) => location.pathname === p || location.pathname.startsWith(`${p}/`))
    .sort((a, b) => b.length - a.length)[0];

  useOutsideClick(menuRef, () => setShowMenu(false));

  const go = (path: string) => {
    setShowMenu(false);
    navigate(path);
  };

  const handleSignOut = () => {
    setShowMenu(false);
    void logout();
  };

  const avatar = (size: string) => (
    <div
      className={cn(
        "flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-primary text-xs font-semibold text-primary-foreground",
        size,
      )}
    >
      {getUserInitials(displayName)}
    </div>
  );

  return (
    <header className="fixed top-0 z-1000 flex h-14 w-full md:hidden items-center justify-between gap-3 border-b border-border bg-background/85 px-3 text-foreground backdrop-blur-xl sm:px-5">
      {/* Left: the MDF mark (white artwork, drawn in ink; white again in dark mode) */}
      <button
        type="button"
        onClick={() => go("/")}
        className="flex shrink-0 cursor-pointer items-center gap-2 rounded-full border-none bg-transparent p-0"
        aria-label={t("header.title")}
      >
        <img src={mdfMark} alt="" className="size-8 object-contain brightness-0 dark:invert sm:size-9" />
        <span className="text-left leading-none font-heading">
          <span className="block text-[19px] font-bold tracking-[-0.04em]">MDF</span>
          <span className="mt-0.5 hidden text-[10px] font-medium text-muted-foreground sm:block">
            {t("header.title")}
          </span>
        </span>
      </button>

      {/* Right: user menu */}
      {signedIn && (
        <div className="flex items-center gap-1.5" ref={menuRef}>
          {/* Platform admins: accounts waiting for approval */}
          {waiting > 0 && (
            <button
              type="button"
              onClick={() => go("/admin?show=waiting")}
              aria-label={`${waiting} waiting for approval`}
              title={`${waiting} waiting for approval`}
              className="relative grid size-9 cursor-pointer place-items-center rounded-full border border-border bg-background transition-colors hover:bg-secondary"
            >
              <Bell className="size-4 text-muted-foreground" />
              <span className="absolute -top-1 -right-1 grid h-4 min-w-4 place-items-center rounded-full bg-status-critical px-1 text-[10px] leading-none font-semibold text-white">
                {waiting > 9 ? "9+" : waiting}
              </span>
            </button>
          )}
          <button
            onClick={() => setShowMenu((p) => !p)}
            className={cn(
              "flex cursor-pointer items-center gap-2 rounded-full border py-1 pr-2.5 pl-1 transition-colors focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:outline-none",
              showMenu ? "border-border bg-secondary" : "border-border bg-background hover:bg-secondary",
            )}
          >
            {avatar("h-7 w-7")}
            <span className="hidden max-w-24 truncate text-xs font-medium sm:inline">{displayName}</span>
            <ChevronDown
              className={cn(
                "h-3 w-3 shrink-0 text-muted-foreground transition-transform duration-200",
                showMenu && "rotate-180",
              )}
            />
          </button>

          {/* Dropdown */}
          {showMenu && (
            <div className="absolute top-[calc(100%+6px)] right-3 z-1001 w-60 overflow-hidden rounded-2xl border bg-popover py-1 text-popover-foreground shadow-[0_24px_60px_rgb(11_18_32/0.14)] sm:right-5">
              {/* User info */}
              <div className="mb-1 border-b px-3 py-2.5">
                <div className="flex items-center gap-2.5">
                  {avatar("h-8 w-8")}
                  <div className="min-w-0">
                    <p className="truncate text-xs font-semibold">{displayName}</p>
                    {email && (
                      <p className="mt-0.5 truncate text-[11px] text-muted-foreground">{email}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* The page links (the rail is hidden on phones) */}
              {links.length > 0 && (
                <div className="border-b px-1 pb-1">
                  {links.map(({ label, path }) => (
                    <button
                      key={path}
                      onClick={() => go(path)}
                      className={cn(
                        "flex w-full cursor-pointer items-center rounded-xl px-2.5 py-2 text-xs transition-colors hover:bg-muted",
                        activePath === path ? "bg-secondary font-semibold" : "text-foreground",
                      )}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              )}

              {/* Menu items */}
              <div className="px-1 pt-1">
                <button
                  onClick={() => go("/profile")}
                  className="flex w-full cursor-pointer items-center gap-2.5 rounded-xl px-2.5 py-2 text-xs text-foreground transition-colors hover:bg-muted"
                >
                  <User className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  {t("header.profile")}
                </button>

                <div className="my-1 border-t" />

                <button
                  onClick={handleSignOut}
                  className="flex w-full cursor-pointer items-center gap-2.5 rounded-xl px-2.5 py-2 text-xs text-status-critical-strong transition-colors hover:bg-status-critical-subtle"
                >
                  <LogOut className="h-3.5 w-3.5 shrink-0" />
                  {t("header.signOut")}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </header>
  );
};

export default Header;
